import cn from 'classnames';
import { FC, MouseEvent, ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, Icon } from '@/shared/ui';

import { ensureRefreshImageSrc } from '@/entities/assets';

import { type ReportPreviewSlide } from '../../config';

import styles from './ReportPreviewSlideshow.module.scss';

type ReportPreviewSlideshowProps = {
  slides: ReportPreviewSlide[];
  actions?: ReactNode;
  className?: string;
};

export const ReportPreviewSlideshow: FC<ReportPreviewSlideshowProps> = (
  props,
) => {
  const { slides, actions, className } = props;

  const { formatMessage } = useIntl();
  const [activeIndex, setActiveIndex] = useState(0);

  const safeIndex = Math.min(activeIndex, slides.length - 1);
  const activeSlide = slides[safeIndex];

  if (!activeSlide) {
    return null;
  }

  const showControls = slides.length > 1;

  const goTo = (event: MouseEvent, index: number) => {
    event.stopPropagation();
    setActiveIndex((index + slides.length) % slides.length);
  };

  // The strip is a control surface: a click on the gap between thumbnails
  // must not fall through to the slide and open the report.
  const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleAuxClick = (event: MouseEvent) => {
    if (event.button === 1) {
      event.stopPropagation();
      activeSlide.openInNewTab();
    }
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-component="report-preview-slideshow"
      title={activeSlide.title}
      onClick={activeSlide.open}
      onAuxClick={handleAuxClick}
    >
      <img
        key={activeSlide.id}
        className={cn(
          styles.image,
          activeSlide.shouldContainPreview && styles.containedImage,
        )}
        src={activeSlide.previewSrc}
        alt={activeSlide.title}
        onError={ensureRefreshImageSrc}
      />

      {actions && (
        <div className={styles.actions} onClick={stopPropagation}>
          {actions}
        </div>
      )}

      <div className={styles.caption}>
        <Icon name={activeSlide.icon} size={22} />
        <span className={cn('h6', styles.captionTitle)}>
          {activeSlide.title}
        </span>
        <span className={cn('p3', styles.captionDate)}>{activeSlide.date}</span>
      </div>

      {showControls && (
        <>
          <div
            className={cn(styles.navZone, styles.prevZone)}
            data-component="report-preview-slideshow-prev"
            onClick={(event) => goTo(event, safeIndex - 1)}
          >
            <Button
              variant="gray"
              size="small"
              circle
              icon="prev"
              className={styles.navButton}
              aria-label={formatMessage({
                id: 'global.back',
                defaultMessage: 'Back',
              })}
              onClick={(event) => goTo(event, safeIndex - 1)}
            />
          </div>

          <div
            className={cn(styles.navZone, styles.nextZone)}
            data-component="report-preview-slideshow-next"
            onClick={(event) => goTo(event, safeIndex + 1)}
          >
            <Button
              variant="gray"
              size="small"
              circle
              icon="next"
              className={styles.navButton}
              aria-label={formatMessage({
                id: 'global.next',
                defaultMessage: 'Next',
              })}
              onClick={(event) => goTo(event, safeIndex + 1)}
            />
          </div>

          <div className={styles.bottomBar}>
            <div className={styles.thumbnails} onClick={stopPropagation}>
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  className={cn(
                    styles.thumbnail,
                    index === safeIndex && styles.activeThumbnail,
                  )}
                  aria-label={slide.title}
                  onClick={(event) => goTo(event, index)}
                >
                  <img
                    className={styles.thumbnailImage}
                    src={slide.previewSrc}
                    alt=""
                    onError={ensureRefreshImageSrc}
                  />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
